import {handleSort} from '@pluto/qc-sort';
import useQuery from '@pluto/query-hook';
import {formQuery} from '@pluto/rest/lib/rest';
import {
    ColumnType,
    getInitialPage,
    getInitialRowsPerPage,
    getInitialSort,
    Sort,
} from '@pluto/table';
import {adminRoutes} from 'App/AppRouter/routes';
import {curry} from 'ramda';
import React, {useEffect, useState} from 'react';
import {Link, useHistory, useLocation} from 'react-router-dom';
import wrap from 'wraptile';
import {Edit, Delete} from '@material-ui/icons';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import List from './List';
import {AppName} from '../Redux/Types';
import './ListContainer.scss';

export interface ListProps {
    rows: AppName[];
    total: number;
    offset: number;
    limit: number;
    isLoading: boolean;
    fetchList: () => void;
    deleteRow: (id: string) => void;
}

const renderDevices = curry((_, {devices}) => devices?.join(', '));

const renderEditLink = curry((_, deleteHandler, {_id, name, devices}) => {
    const cellStyle = {
        width: '60px',
    };
    
    return (
        <div style={cellStyle}>
            <Link to={`/app-name/${_id}`}>
                <Edit />
            </Link>
            &nbsp;&nbsp;
            <span
                role="button"
                onClick={() => deleteHandler({
                    _id,
                    name,
                    devices,
                })}
                style={{
                    cursor: 'pointer',
                }}
            >
                <Delete />
            </span>
        </div>
    );
});

export const handleDebounceCallback = curry((handlers, title) => {
    const {fetchList, setPage, setQuery, history, ...params} = handlers;
    const newQuery = {
        ...params,
        title,
    };
    
    setPage(0);
    setQuery(history, newQuery);
    fetchList(newQuery);
});

export const handleListPaging = curry((containerArgs, tablePaging) => {
    const {fetchList, setQuery, history, ...containerParams} = containerArgs;
    const params = {
        ...containerParams,
        ...tablePaging,
    };
    
    setQuery(history, params);
    fetchList(params);
});

export const setQuery = (history: any, params: any) => {
    const search = formQuery('', params);
    
    history.push({
        pathname: adminRoutes.appNameListPage,
        search,
    });
};

export const handleMount = wrap((props: any) => {
    const {fetchList, setPage, setRowsPerPage, params, query} = props;
    
    const queryLimit = query.get('limit');
    
    if (queryLimit) {
        const queryOffset = query.get('offset');
        const limit = Number(queryLimit);
        const page = Number(queryOffset) / limit || 0;
        
        setRowsPerPage(limit);
        setPage(page);
        fetchList({
            ...params,
            limit: queryLimit,
        });
        return;
    }
    
    fetchList({
        ...params,
        limit: params.limit,
    });
});

export default (props: ListProps) => {
    const {rows, total, fetchList, deleteRow} = props;
    const query = useQuery();
    const history = useHistory();
    const {search} = useLocation();
    const [title] = useState(query.get('title') || '');
    const [filters] = useState({});
    const initialRowsPerPage = getInitialRowsPerPage(query);
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
    const initialPage = getInitialPage({
        query,
        rowsPerPage,
    });
    const [page, setPage] = useState(initialPage);
    const initialSort = getInitialSort(query);
    const [sort, setSort] = useState<Sort>(initialSort);
    
    const [appNameToDelete, setAppNameToDelete] = useState<Partial<AppName> | null>(null);
    
    const handleDeleteCancel = () => {
        setAppNameToDelete(null);
    };
    
    const handleDeleteConfirm = () => {
        if (appNameToDelete?._id) {
            deleteRow(appNameToDelete._id);
            setAppNameToDelete(null);
            setTimeout(fetchList, 100);
        }
    };
    
    const onDeleteHandler = (appName: Partial<AppName>) => {
        setAppNameToDelete(appName);
    };
    
    const cols: ColumnType[] = [
        {
            dataKey: 'name',
            title: 'App Name',
        },
        {
            dataKey: 'devices',
            title: 'Associated Devices',
            render: renderDevices(search),
        },
        {
            dataKey: 'id',
            title: 'Actions',
            render: renderEditLink(search, onDeleteHandler),
        },
    ];
    
    const params = {
        ...filters,
        title,
        offset: rowsPerPage * page,
        limit: rowsPerPage,
    };
    
    const onPaging = handleListPaging({
        ...params,
        fetchList,
        setQuery,
        history,
    });
    
    const onSort = handleSort({
        setSort,
        getList: fetchList,
        params,
        setQuery,
        history,
        route: adminRoutes.appNameListPage,
    });
    
    const onMount = handleMount({
        fetchList,
        setRowsPerPage,
        setPage,
        params,
        query,
    });
    
    useEffect(onMount, []);
    
    return (
        <>
            <List
                columns={cols}
                data={rows}
                total={total}
                page={page}
                setSort={onSort}
                sort={sort}
                onPaging={onPaging}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
            />
            
            <Dialog
                open={appNameToDelete !== null}
                onClose={handleDeleteCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Delete App profile &quot;{appNameToDelete?.name}&quot;
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete App profile “{appNameToDelete?.name}” ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="primary"
                    >
                        Confirm
                    </Button>
                    <Button
                        className="buttons_cancel"
                        onClick={handleDeleteCancel}
                        variant="contained"
                        autoFocus
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
